// acquire-settings-file-lock-raw.ts
//
// TẦNG THẤP NHẤT của lock #164: tranh chấp một LOCK FILE giữa các TIẾN TRÌNH.
// Tách khỏi with-settings-file-lock.ts để cả hai file dưới ngưỡng 200 dòng
// (phase-02 R7/AC7) — seam tự nhiên: file này chỉ biết `lockPath`, không biết
// workspace, không có hàng đợi in-process, không phát hiện lồng.
//
// Điểm vào công khai của ticket vẫn là `withSettingsFileLock`; mọi thứ ở đây được
// re-export qua with-settings-file-lock.ts để call site chỉ cần nhớ MỘT module.

import { randomUUID } from "node:crypto";
import { promises as fs } from "node:fs";
import { hostname } from "node:os";
import { basename, dirname } from "node:path";
import { ensureDir } from "./filesystem-helpers.js";
import { log } from "./terminal-logger.js";

// Fail-CLOSED: hết ngân sách chờ ⇒ throw, KHÔNG ghi đè. Call site quyết định bỏ
// qua hay lan lỗi (xem reapply-enabled-tools-after-sync.ts).
export class SettingsLockTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SettingsLockTimeoutError";
  }
}

// Gọi lồng = lỗi lập trình, không phải tranh chấp ⇒ báo NGAY thay vì treo hết
// timeout rồi mới lộ (self-deadlock rất khó chẩn đoán).
export class SettingsLockReentrancyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SettingsLockReentrancyError";
  }
}

export const DEFAULT_LOCK_TIMEOUT_MS = 10_000;
const DEFAULT_STALE_MS = 30_000;
const DEFAULT_RETRY_MS = 50;

export interface SettingsFileLockOptions {
  /** Ngân sách TỔNG tính từ lúc gọi — GỒM cả thời gian chờ hàng đợi in-process. */
  timeoutMs?: number;
  /** Lock cũ hơn mốc này (theo mtime) coi là mồ côi và bị thu hồi. */
  staleMs?: number;
  retryMs?: number;
  /** @internal Deadline tuyệt đối do `withSettingsFileLock` tính TRƯỚC khi xếp hàng. */
  deadlineAt?: number;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Tuổi lock theo MTIME, KHÔNG theo pid: pid bị tái sử dụng, và trên thư mục chia
// sẻ giữa nhiều máy thì pid vô nghĩa. undefined = lock vừa biến mất ⇒ thử lại.
async function lockAgeMs(lockPath: string): Promise<number | undefined> {
  try {
    const stat = await fs.stat(lockPath);
    return Date.now() - stat.mtimeMs;
  } catch {
    return undefined;
  }
}

// Kẻ thắng cuộc thu hồi có thể chết giữa chừng và để lại token `.stealing-*`.
// Best-effort dọn rác; không được làm acquire thất bại.
async function sweepStaleStealTokens(lockPath: string, staleMs: number): Promise<void> {
  const prefix = `${basename(lockPath)}.stealing-`;
  const dir = dirname(lockPath);
  try {
    for (const name of await fs.readdir(dir)) {
      if (!name.startsWith(prefix)) continue;
      const tokenPath = `${dir}/${name}`;
      const stat = await fs.stat(tokenPath).catch(() => undefined);
      if (stat && Date.now() - stat.mtimeMs > staleMs) {
        await fs.unlink(tokenPath).catch(() => {});
      }
    }
  } catch {
    // thư mục biến mất giữa chừng — không phải lỗi của người đang acquire
  }
}

// THU HỒI NGUYÊN TỬ (chốt b2): `rename` sang tên CÓ ĐỊNH DANH. Chỉ MỘT kẻ rename
// thành công; kẻ thua nhận ENOENT và **không xoá gì cả**, phải quay lại vòng lặp.
// ⛔ TUYỆT ĐỐI KHÔNG `unlink` rồi `open('wx')`: `unlink` của kẻ thua là VÔ ĐIỀU KIỆN,
// nên nếu nó chạy SAU khi kẻ thắng đã tạo lock mới thì nó xoá đúng lock TƯƠI đó ⇒
// CẢ HAI vào critical section ⇒ lost update quay lại y nguyên.
//
// ⚠️ Cửa sổ CÒN LẠI — nói thẳng để không ai tưởng đây là bất khả xâm phạm: giữa lúc
// `stat` báo stale và lúc `rename` thực thi, lock có thể đã được kẻ khác thu hồi +
// tạo mới ⇒ `rename` này cướp đúng lock TƯƠI. Cửa sổ cỡ micro-giây VÀ đòi hỏi tiến
// trình cũ đã chết đúng `staleMs` trước đó — hẹp hơn b1 nhiều bậc (b1 hở ở MỌI kẻ
// thua, không chỉ kẻ trúng đúng micro-giây). KHÔNG vá thêm: bản vá duy nhất đúng
// ("trả lock lại nếu phát hiện vừa cướp lock tươi") không test được deterministic
// bằng 1 tiến trình ⇒ thành code không ai canh. Xem
// reports/control-proof-phase-02.md §2: case 7 ĐÃ ĐO LÀ KHÔNG phân biệt được b1/b2.
async function stealStaleLock(lockPath: string, ageMs: number): Promise<void> {
  const token = `${lockPath}.stealing-${process.pid}-${randomUUID()}`;
  try {
    await fs.rename(lockPath, token);
  } catch {
    return; // kẻ khác thu hồi trước — KHÔNG tạo lock, để vòng lặp thử lại
  }
  // Chỉ log đường dẫn + tuổi lock. KHÔNG log nội dung settings (chứa API key).
  log.warn(
    `settings.json lock mồ côi (${Math.round(ageMs / 1000)}s) — thu hồi: ${lockPath}. ` +
      "Nếu thấy dòng này thường xuyên, có tiến trình avatar bị kill giữa lúc ghi.",
  );
  await fs.unlink(token).catch(() => {});
}

/**
 * @internal CHỈ dùng cho test tầng tranh-chấp-file (2 kẻ thu hồi cùng 1 lock stale).
 * KHÔNG có hàng đợi in-process (R9a), KHÔNG có phát hiện lồng (R9b) — đi qua API
 * công khai thì hàng đợi đã tuần tự hoá xong, không còn tranh chấp nào để đo.
 */
export async function acquireSettingsFileLockRaw(
  lockPath: string,
  opts: SettingsFileLockOptions = {},
): Promise<{ release: () => Promise<void> }> {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_LOCK_TIMEOUT_MS;
  const staleMs = opts.staleMs ?? DEFAULT_STALE_MS;
  const retryMs = opts.retryMs ?? DEFAULT_RETRY_MS;
  const deadlineAt = opts.deadlineAt ?? Date.now() + timeoutMs;
  // BẮT BUỘC là bước ĐẦU: writer #9 chạy trong `avatar init` khi `.claude/` CHƯA
  // tồn tại; thiếu bước này thì open('wx') nhận ENOENT và `init` sạch nổ.
  await ensureDir(dirname(lockPath));

  for (;;) {
    try {
      // O_CREAT|O_EXCL — atomic trên cả POSIX lẫn Windows (#142 giữ Windows sống).
      const handle = await fs.open(lockPath, "wx");
      try {
        // Nội dung chỉ để CHẨN ĐOÁN khi kẹt: không có bí mật nào ở đây.
        await handle.writeFile(
          JSON.stringify({
            pid: process.pid,
            startedAt: new Date().toISOString(),
            host: hostname(),
          }),
        );
      } finally {
        await handle.close();
      }
      return {
        release: async () => {
          // Idempotent + không throw: `unlink` fail (Windows file-locking) thì
          // stale-steal là lưới an toàn tầng 2.
          await fs.unlink(lockPath).catch(() => {});
        },
      };
    } catch (err) {
      if ((err as NodeJS.ErrnoException).code !== "EEXIST") throw err;
    }

    await sweepStaleStealTokens(lockPath, staleMs);
    const age = await lockAgeMs(lockPath);
    if (age !== undefined && age > staleMs) {
      await stealStaleLock(lockPath, age);
      continue; // thử lại NGAY, không tính vào nhịp retry
    }
    if (Date.now() >= deadlineAt) {
      throw new SettingsLockTimeoutError(
        `Chờ quá ${timeoutMs}ms mà settings.json vẫn bị tiến trình khác giữ (${lockPath}).`,
      );
    }
    await sleep(retryMs);
  }
}
